import { z } from 'zod';
import { DomainError } from '../errors.js';
import { parseLocalDate } from '../local-date.js';
import { type RaLibraryContent, raLibraryChecks } from './library.js';
import type { RaAcceptance, RaAcceptor } from './matrix.js';

/**
 * Gemide operasyonel risk doğrulaması (K5; ADR 0089). Gemi zabiti planlanan operasyon için gemiye
 * atanmış güncel şirket RA sürümünü seçer; sürümle yayımlanan checklist'i (altı ortak madde,
 * faaliyete özel maddeler ve RA'nın zorunlu kontrolleri) cevaplar. Altı "Yes" yetki değildir:
 * sonuç Pending Authorization'dır; yetkiyi kaptan ya da yetkili zabit kapsamı içinde verir
 * (FD §9, §11; SS §5–§8; BR-01…BR-08).
 *
 * Durum saklanmaz: değiştirilemez olay izinden (`ra_verification_events`) ve geçerli turun
 * cevaplarından türer. Modül saftır: saat parametreyle gelir.
 */

/** Operasyon ve imza türü (ADR 0075): yetki kararında parola yalnız şirket ayarı açıkken. */
export const RA_OPERATION_RECORD = 'ra_operation';

/** Altı ortak madde (SS §5; FD §9): sırası ve anahtarı sabittir; metni dilden gelir. */
export const RA_COMMON_CHECKS = [
  'valid_ra',
  'crew_briefed',
  'equipment_ready',
  'conditions_assessed',
  'controls_implemented',
  'no_uncontrolled_hazards',
] as const;
export type RaCommonCheck = (typeof RA_COMMON_CHECKS)[number];

/** Ortak maddelerin sürümü: madde listesi değişirse artar, eski doğrulama eskisini taşır. */
export const RA_CHECKLIST_COMMON_VERSION = 1;

/** "No" cevabı yükseltme açan ortak madde (FD §9: "No opens new-hazard finding and escalation"). */
const ESCALATING_CHECK = 'common:no_uncontrolled_hazards';

export type RaChecklistSource = 'common' | 'activity' | 'control';

export interface RaChecklistItem {
  /** `common:<anahtar>`, `check:<madde>` ya da `control:<satır>:<kontrol>`. */
  key: string;
  source: RaChecklistSource;
  /** Faaliyete özel madde ve kontrol metni; ortak maddede `null` (dilden). */
  text: string | null;
  /** Kontrol maddesinin bağlamı: satırın adımı ve tehlikesi. */
  context: string | null;
  /** N/A verilebilir mi (BR-04): yalnız şirketin işaretlediği faaliyete özel madde. */
  naAllowed: boolean;
}

/** Doğrulamanın anlık görüntüsü: hangi RA sürümünün hangi maddeleri (FD §11 "checklist version"). */
export interface RaChecklist {
  versionId: string;
  code: string;
  revision: string;
  /** RA sürümünün içerik hash'i. */
  contentHash: string;
  common: number;
  items: RaChecklistItem[];
}

/**
 * Sürümün checklist'i (R17; sahip kararı 2026-10-19, soru 4): altı ortak madde, sürümdeki
 * faaliyete özel maddeler (isteğe bağlı) ve satırlardaki "mandatory" işaretli mevcut kontroller.
 * Koşullu kontrol madde olmaz. Ortak madde ve kontrol kritiktir: N/A yok (BR-04).
 */
export function raChecklist(version: {
  id: string;
  code: string;
  revision: string;
  contentHash: string;
  content: Pick<RaLibraryContent, 'rows' | 'checks'>;
}): RaChecklist {
  const items: RaChecklistItem[] = RA_COMMON_CHECKS.map((key) => ({
    key: `common:${key}`,
    source: 'common',
    text: null,
    context: null,
    naAllowed: false,
  }));
  for (const check of raLibraryChecks(version.content)) {
    items.push({
      key: `check:${check.id}`,
      source: 'activity',
      text: check.text,
      context: null,
      naAllowed: check.naAllowed,
    });
  }
  for (const row of version.content.rows) {
    for (const control of row.existingControls) {
      if (control.requirement !== 'mandatory') continue;
      items.push({
        key: `control:${row.id}:${control.id}`,
        source: 'control',
        text: control.measure,
        context: `${row.step} — ${row.hazard}`,
        naAllowed: false,
      });
    }
  }
  return {
    versionId: version.id,
    code: version.code,
    revision: version.revision,
    contentHash: version.contentHash,
    common: RA_CHECKLIST_COMMON_VERSION,
    items,
  };
}

// ---------------------------------------------------------------------------------------------
// Cevap (SS §5, BR-04)

export const RA_ANSWERS = ['yes', 'no', 'na'] as const;
export type RaAnswer = (typeof RA_ANSWERS)[number];

export const RA_ANSWER_COMMENT_MAX = 1000;
export const RA_ANSWER_EVIDENCE_MAX = 500;

export interface RaAnswerInput {
  itemKey: string;
  answer: RaAnswer;
  comment: string | null;
  evidence: string | null;
}

const trimmed = (value: string | null | undefined) => {
  const v = (value ?? '').normalize('NFC').trim();
  return v === '' ? null : v;
};

/**
 * Cevabı denetler ve sadeleştirir: madde checklist'te (`UNKNOWN_CHECK_ITEM`), cevap Yes/No/N/A,
 * N/A yalnız uygun maddede (`NA_NOT_ALLOWED`), No ve N/A gerekçeli (`COMMENT_REQUIRED`). Kanıt
 * isteğe bağlı nottur (fotoğraf ve dosya K6'nın bulgusunda).
 */
export function raAnswerOrThrow(
  item: RaChecklistItem | undefined,
  input: RaAnswerInput,
): RaAnswerInput {
  if (!item || item.key !== input.itemKey) {
    throw new DomainError('UNKNOWN_CHECK_ITEM', String(input.itemKey));
  }
  if (!(RA_ANSWERS as readonly string[]).includes(input.answer)) {
    throw new DomainError('VALIDATION_FAILED', String(input.answer), 'answer');
  }
  if (input.answer === 'na' && !item.naAllowed) throw new DomainError('NA_NOT_ALLOWED', item.key);
  const comment = trimmed(input.comment);
  const evidence = trimmed(input.evidence);
  if (input.answer !== 'yes' && comment === null) {
    throw new DomainError('COMMENT_REQUIRED', item.key);
  }
  if ((comment?.length ?? 0) > RA_ANSWER_COMMENT_MAX) {
    throw new DomainError('TOO_LONG', item.key, 'comment');
  }
  if ((evidence?.length ?? 0) > RA_ANSWER_EVIDENCE_MAX) {
    throw new DomainError('TOO_LONG', item.key, 'evidence');
  }
  return { itemKey: item.key, answer: input.answer, comment, evidence };
}

/** Gönderimin sonucu: Pending Authorization, On Hold ya da Escalated (FD §11). */
export type RaSubmitOutcome = 'pending_authorization' | 'on_hold' | 'escalated';

export interface RaVerificationOutcome {
  answered: number;
  total: number;
  /** Cevapsız maddeler, checklist sırasıyla (BR-01). */
  missing: string[];
  /** "No" cevaplı maddeler (BR-03). */
  failed: string[];
  /** Eksik yoksa gönderimin sonucu; eksik varsa `null`. */
  next: RaSubmitOutcome | null;
}

/**
 * Turun cevaplarından sonuç (BR-01…BR-03): her madde cevaplanmadan gönderilmez; bütün maddeler
 * Yes ya da uygun N/A ise Pending Authorization (asla Authorized değil); bir "No" yetkiyi kapatır
 * (On Hold); "No additional uncontrolled hazards" maddesinin "No"su yükseltmedir (Escalated).
 */
export function raVerificationOutcome(
  items: readonly RaChecklistItem[],
  answers: ReadonlyMap<string, { answer: RaAnswer }>,
): RaVerificationOutcome {
  const missing: string[] = [];
  const failed: string[] = [];
  for (const item of items) {
    const answer = answers.get(item.key)?.answer;
    if (answer === undefined) missing.push(item.key);
    else if (answer === 'no') failed.push(item.key);
  }
  const next: RaSubmitOutcome | null =
    missing.length > 0
      ? null
      : failed.includes(ESCALATING_CHECK)
        ? 'escalated'
        : failed.length > 0
          ? 'on_hold'
          : 'pending_authorization';
  return { answered: items.length - missing.length, total: items.length, missing, failed, next };
}

// ---------------------------------------------------------------------------------------------
// Durum makinesi (R18; FD §11; SS §6)

/** Önerilen 13 durum (SS §6). İlk üçü açık (düzenlenebilir) aşamanın cevaba göre adıdır. */
export const RA_VERIFICATION_STATUSES = [
  'draft',
  'incomplete',
  'verification_completed',
  'pending_authorization',
  'on_hold',
  'escalated',
  'authorized',
  'in_progress',
  'suspended',
  'completed',
  'closed',
  'cancelled',
  'reverification_required',
] as const;
export type RaVerificationStatus = (typeof RA_VERIFICATION_STATUSES)[number];

/** Değiştirilemez olay izinin olayları (`ra_verification_events.kind`). */
export const RA_VERIFICATION_EVENTS = [
  'submitted',
  'returned',
  'rejected',
  'authorized',
  'started',
  'conditions_changed',
  'stopped',
  'reverification_started',
  'completed',
  'closed',
  'cancelled',
  'briefed',
] as const;
export type RaVerificationEvent = (typeof RA_VERIFICATION_EVENTS)[number];

/** Gerekçe isteyen olaylar (FD §11 "Return/reject require reason"; BR-07; BR-09). */
export const RA_REASONED_EVENTS: readonly RaVerificationEvent[] = [
  'returned',
  'rejected',
  'conditions_changed',
  'stopped',
  'cancelled',
  'completed',
];

/** Olay izinden türeyen temel durum; `open` düzenlenebilir aşamadır. */
type Base =
  | 'open'
  | Exclude<RaVerificationStatus, 'draft' | 'incomplete' | 'verification_completed'>;

const ACTIVE: readonly Base[] = [
  'open',
  'pending_authorization',
  'on_hold',
  'escalated',
  'authorized',
  'in_progress',
  'suspended',
  'reverification_required',
];

const TRANSITIONS: Readonly<
  Record<RaVerificationEvent, { from: readonly Base[]; to: Base | 'outcome' | null }>
> = {
  submitted: { from: ['open'], to: 'outcome' },
  returned: { from: ['pending_authorization'], to: 'open' },
  rejected: { from: ['pending_authorization'], to: 'cancelled' },
  authorized: { from: ['pending_authorization'], to: 'authorized' },
  started: { from: ['authorized'], to: 'in_progress' },
  // Önemli koşul değişikliği yetkiyi düşürür (BR-07; FD §11)
  conditions_changed: { from: ['authorized', 'in_progress'], to: 'reverification_required' },
  // İş durdurma (SS §1: kaptanın ve mürettebatın iş durdurma yetkisi her zaman korunur)
  stopped: { from: ['pending_authorization', 'authorized', 'in_progress'], to: 'suspended' },
  // Düzeltme "No"yu silmez: yeniden doğrulama yeni turdur (BR-08)
  reverification_started: {
    from: ['on_hold', 'escalated', 'suspended', 'reverification_required'],
    to: 'open',
  },
  completed: { from: ['in_progress'], to: 'completed' },
  closed: { from: ['completed'], to: 'closed' },
  // Süren operasyon iptal edilmez: önce durdurulur
  cancelled: {
    from: [
      'open',
      'pending_authorization',
      'on_hold',
      'escalated',
      'authorized',
      'suspended',
      'reverification_required',
    ],
    to: 'cancelled',
  },
  // Mürettebatın brifing onayı durumu değiştirmez (SS §4 Crew "acknowledge briefings")
  briefed: { from: ACTIVE, to: null },
};

export interface RaVerificationEventRecord {
  kind: RaVerificationEvent;
  round: number;
  createdBy: string;
  createdAt: string;
  /** Yalnız `submitted`: o anki sonuç. */
  outcome: RaSubmitOutcome | null;
  /** Yalnız `authorized`: yetkinin bitişi (UTC). */
  validUntil: string | null;
}

export interface RaVerificationState {
  status: RaVerificationStatus;
  /** Olay izinden temel durum (açık aşama `open`). */
  base: Base;
  /** Doğrulama turu: 1'den başlar, yeniden doğrulamada artar. */
  round: number;
  /** Başlık ve cevaplar düzenlenebilir mi (açık aşama). */
  editable: boolean;
  /** Açık aşama iadeyle mi açıldı. */
  returned: boolean;
  /** Geçerli turu son gönderen. */
  submittedBy: string | null;
  /** Yürürlükteki yetkiyi veren; yetki düşünce `null`. */
  authorizedBy: string | null;
  validUntil: string | null;
  /** Yetkinin süresi geçti: Reverification Required görünür. */
  expired: boolean;
  /** Brifing onayı veren kişiler, ilk onay sırasıyla. */
  briefed: string[];
}

function openStatus(progress: { answered: number; total: number }): RaVerificationStatus {
  if (progress.answered === 0) return 'draft';
  return progress.answered < progress.total ? 'incomplete' : 'verification_completed';
}

/**
 * Olay izinden ve geçerli turun cevap sayısından durum (R18). Olaylar yazılış sırasıyla gelir.
 * Yetkinin süresi `now`'a göre: geçmişse durum Reverification Required'dır (SS §8 "authorization
 * must be tied to a specific operation and conditions, not treated as perpetual").
 */
export function raVerificationState(
  events: readonly RaVerificationEventRecord[],
  progress: { answered: number; total: number },
  now: string,
): RaVerificationState {
  let base: Base = 'open';
  let round = 1;
  let returned = false;
  let submittedBy: string | null = null;
  let authorizedBy: string | null = null;
  let validUntil: string | null = null;
  const briefed: string[] = [];
  for (const event of events) {
    const rule = TRANSITIONS[event.kind];
    if (event.kind === 'briefed') {
      if (!briefed.includes(event.createdBy)) briefed.push(event.createdBy);
      continue;
    }
    if (event.kind === 'submitted') {
      base = event.outcome ?? 'pending_authorization';
      submittedBy = event.createdBy;
      returned = false;
      continue;
    }
    if (event.kind === 'authorized') {
      authorizedBy = event.createdBy;
      validUntil = event.validUntil;
    } else if (event.kind !== 'started' && event.kind !== 'completed' && event.kind !== 'closed') {
      // Durdurma, koşul değişikliği, iade, red, iptal ve yeniden doğrulama yetkiyi düşürür
      authorizedBy = null;
      validUntil = null;
    }
    if (event.kind === 'reverification_started') {
      round = event.round;
      submittedBy = null;
    }
    returned = event.kind === 'returned';
    if (rule.to !== null && rule.to !== 'outcome') base = rule.to;
  }
  const expired =
    base === 'authorized' && validUntil !== null && Date.parse(validUntil) <= Date.parse(now);
  const status: RaVerificationStatus =
    base === 'open' ? openStatus(progress) : expired ? 'reverification_required' : base;
  return {
    status,
    base,
    round,
    editable: base === 'open',
    returned,
    submittedBy,
    authorizedBy,
    validUntil,
    expired,
    briefed,
  };
}

/**
 * Olayın geçerli olup olmadığı ve sonraki durum (sunucuda denetlenir; SS §6 "disallow impossible
 * transitions"). `submitted` için sonuç verilir: eksik cevapta `CHECKS_INCOMPLETE` (BR-01).
 * Süresi geçen yetki başlatılamaz (`AUTHORIZATION_EXPIRED`); yeniden doğrulama ister.
 */
export function raVerificationNext(
  state: RaVerificationState,
  kind: RaVerificationEvent,
  outcome?: RaSubmitOutcome | null,
): RaVerificationStatus {
  const rule = TRANSITIONS[kind];
  const effective: Base = state.expired ? 'reverification_required' : state.base;
  if (!rule.from.includes(effective)) {
    if (state.expired && rule.from.includes('authorized')) {
      throw new DomainError('AUTHORIZATION_EXPIRED', kind);
    }
    throw new DomainError('INVALID_TRANSITION', `${state.status} → ${kind}`);
  }
  if (kind === 'submitted') {
    if (outcome === undefined || outcome === null) throw new DomainError('CHECKS_INCOMPLETE');
    return outcome;
  }
  if (rule.to === null) return state.status;
  if (rule.to === 'open')
    return kind === 'reverification_started' ? 'draft' : 'verification_completed';
  return rule.to as RaVerificationStatus;
}

/** Olayın geçerli olduğu durum mu (arayüzün düğmeleri). */
export function raVerificationEventAllowed(
  state: RaVerificationState,
  kind: RaVerificationEvent,
): boolean {
  const effective: Base = state.expired ? 'reverification_required' : state.base;
  return TRANSITIONS[kind].from.includes(effective);
}

// ---------------------------------------------------------------------------------------------
// Yetki kapsamı ve geçerlilik (R21, R22; FD §11; SS §8; BR-05)

/**
 * Kalan riskin bandına göre yetki kapsamı (sahip kararı 2026-10-19, soru 3):
 * - Critical (kabul edilemez) işi durdurur: yetki verilmez (`RESIDUAL_UNACCEPTABLE`).
 * - High (yükseltme) bandında kabul edenler arasında kaptan varsa yetkiyi kaptan verir; bu kaptanın
 *   kalan risk kabulüdür (`MASTER_ACCEPTANCE_REQUIRED`). Ofisteki kabul (DPA) sürüm yayımlanırken
 *   alınmıştır (ADR 0087 §3).
 * - Öteki bantlarda `ra_operations.approve` yetkili zabit yeter.
 */
export function raAuthorizationScope(input: {
  acceptance: RaAcceptance | null;
  acceptedBy: readonly RaAcceptor[];
  authorizerIsMaster: boolean;
}): { masterAcceptance: boolean } {
  if (input.acceptance === 'unacceptable') throw new DomainError('RESIDUAL_UNACCEPTABLE');
  if (input.acceptance === 'escalation' && input.acceptedBy.includes('master')) {
    if (!input.authorizerIsMaster) throw new DomainError('MASTER_ACCEPTANCE_REQUIRED');
    return { masterAcceptance: true };
  }
  return { masterAcceptance: false };
}

/**
 * Yetkinin en uzun geçerliliği, saat (ajan kararı, ADR 0089 §4): kesin üst sınır. Şirket ayarı
 * daha kısa bir sınır seçebilir (K7; ADR 0095 §3), uzatamaz.
 */
export const RA_AUTHORIZATION_MAX_HOURS = 24;
const HOUR = 3_600_000;

/** Ayardan gelen sınır 1…24 saate kırpılır; geçersizse kesin sınır. */
function maxHoursOf(maxHours: number | undefined): number {
  return typeof maxHours === 'number' && Number.isInteger(maxHours) && maxHours >= 1
    ? Math.min(maxHours, RA_AUTHORIZATION_MAX_HOURS)
    : RA_AUTHORIZATION_MAX_HOURS;
}

/**
 * Varsayılan geçerlilik: planlanan bitiş, en çok `maxHours` (varsayılan 24) saat; bitiş yoksa ya
 * da geçmişse sınırın kendisi.
 */
export function raDefaultValidUntil(
  now: string,
  plannedEnd: string | null,
  maxHours?: number,
): string {
  const limit = Date.parse(now) + maxHoursOf(maxHours) * HOUR;
  const end = plannedEnd ? Date.parse(plannedEnd) : Number.NaN;
  const value = Number.isFinite(end) && end > Date.parse(now) && end <= limit ? end : limit;
  return new Date(value).toISOString();
}

/** Geçerlilik `now`'dan sonra ve en çok `maxHours` (varsayılan 24) saat (`VALIDITY_OUT_OF_RANGE`). */
export function assertRaValidUntil(now: string, validUntil: string, maxHours?: number): void {
  const value = Date.parse(validUntil);
  const start = Date.parse(now);
  if (!Number.isFinite(value) || value <= start || value > start + maxHoursOf(maxHours) * HOUR) {
    throw new DomainError('VALIDITY_OUT_OF_RANGE', validUntil);
  }
}

// ---------------------------------------------------------------------------------------------
// Başlık, numara ve içerik

const instant = z.iso
  .datetime({ offset: false, error: 'INVALID_DATETIME' })
  .nullish()
  .transform((v) => v ?? null);

/** Planlanan operasyon (FD §9 "vessel > activity > planned operation"; SS §6 adım 1). */
export const raVerificationHeaderSchema = z
  .strictObject({
    operation: z
      .string({ error: 'REQUIRED' })
      .transform((v) => v.normalize('NFC').trim().replace(/\s+/g, ' '))
      .pipe(z.string().min(1, 'REQUIRED').max(300, 'TOO_LONG')),
    location: z
      .string({ error: 'INVALID' })
      .transform((v) => v.normalize('NFC').trim())
      .pipe(z.string().max(200, 'TOO_LONG'))
      .nullish()
      .transform((v) => (v ? v : null)),
    plannedStart: instant,
    plannedEnd: instant,
  })
  .refine(
    (h) =>
      h.plannedStart === null ||
      h.plannedEnd === null ||
      Date.parse(h.plannedEnd) > Date.parse(h.plannedStart),
    { message: 'END_BEFORE_START', path: ['plannedEnd'] },
  );
export type RaVerificationHeader = z.output<typeof raVerificationHeaderSchema>;

/** Biçim hatası `INVALID_RA_VERIFICATION` (ayrıntı alan:kod). */
export function raVerificationHeaderOrThrow(input: unknown): RaVerificationHeader {
  const parsed = raVerificationHeaderSchema.safeParse(input);
  if (!parsed.success) {
    const issues = parsed.error.issues.map((i) => `${i.path.map(String).join('.')}:${i.message}`);
    throw new DomainError(
      'INVALID_RA_VERIFICATION',
      issues.join(','),
      parsed.error.issues[0]?.path.map(String).join('.'),
    );
  }
  return parsed.data;
}

/** Doğrulama numarası gemi ve gemi yılı başına: `OV-YYYY-NNN`. */
export function raVerificationNumber(
  day: string,
  lastSeq: number,
): { year: number; seq: number; number: string } {
  const { year } = parseLocalDate(day);
  if (!Number.isInteger(lastSeq) || lastSeq < 0) {
    throw new DomainError('VALIDATION_FAILED', `last_seq ${lastSeq}`, 'seq');
  }
  const seq = lastSeq + 1;
  return {
    year,
    seq,
    number: `OV-${String(year).padStart(4, '0')}-${String(seq).padStart(3, '0')}`,
  };
}

export const RA_VERIFICATION_NUMBER = /^OV-\d{4}-\d{3,}$/;

/**
 * Gönderim ve yetki kararının hash'lendiği içerik (FD §11 "RA version; checklist version";
 * SS §8 "record hash"): başlık, RA sürümü, checklist hash'i, tur ve turun son cevapları (madde
 * anahtarına göre sıralı). Hash'i `packages/db` hesaplar (JCS + SHA-256).
 */
export function raVerificationContent(input: {
  id: string;
  vesselId: string;
  number: string;
  versionId: string;
  checklistHash: string;
  header: RaVerificationHeader;
  round: number;
  answers: readonly RaAnswerInput[];
}) {
  return {
    id: input.id,
    vesselId: input.vesselId,
    number: input.number,
    versionId: input.versionId,
    checklistHash: input.checklistHash,
    operation: input.header.operation,
    location: input.header.location,
    plannedStart: input.header.plannedStart,
    plannedEnd: input.header.plannedEnd,
    round: input.round,
    answers: [...input.answers]
      .sort((a, b) => a.itemKey.localeCompare(b.itemKey))
      .map((a) => ({
        itemKey: a.itemKey,
        answer: a.answer,
        comment: a.comment,
        evidence: a.evidence,
      })),
  };
}
